import {Component, ElementRef, inject, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import { faMagnifyingGlass, faChevronRight, faChevronLeft, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {ContentService} from "../../../data-access/content.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {checkForHTMLTags} from "../../../../shared/utils/checkForHTMLTags";
import {SampleCase} from "../../../../shared/ui/code-editor/code-editor.component";

export interface Answer {
  value: string
  formatted: string
  mathjax: boolean
  sort: number
  correct: number
  choice: Choice
  mode?: string
  string?: string
  description?: string
  distractor_id?: number
}
interface Choice {
  display: string
  display_order: Answer[]
}
interface Value {
  pairswith: string[]
  value: string
  formatted: string
  mathjax: boolean
  sort: number
  correct: number
  description?: string
  distractor_id: null
}
interface Cluster {
  clusterPrefix: string
  clusterPostfix: string
  inline: boolean
  formatPostfix: string
  id: string
  type: string
  fields: Field[]
  label?: string
  recordedAnswer?: string
  recordedCorrectness?: string
  correctAnswer?: string
  steps?: string
}
interface Field {
  formatPrefix: string
  answers: Answer[]
  formatPostfix: string
  id: string
  type: string
  recordedAnswer?: string
}
interface Item {
  formatted: string
  value: string
  orientation: string
}
interface Group {
  formatted: string
  value: string
  orientation: string
  examples: Item[]
}
interface Detail {
  allItems: Item[]
  allGroups: Group[]
  // Some endpoints nest these under a "group" key alongside the other per-type detail keys.
  group?: {
    allItems: Item[]
    allGroups: Group[]
  }
}
export interface Audio {
  source: string
  player_id: string
  plays_spent: number
  plays_allowed?: number
}
export interface FullProblemTemplate {
  argumentNames?: string[];
  answers: Answer[]
  values: Value[]
  reasons: Value[]
  clusters: Cluster[]
  details: Detail
  type: string
  question_text: string
  mathjax: boolean
  answerSkeleton: string[]
  sampleCases: SampleCase[]
  samplesInitial: number
  samplesRemaining: number
  problemtemplate_id: number
  problemtemplate_name: string
  learningobjective_id: number
  learningobjective_name?: string
  uuid?: number
  recordedAnswer?: string
  recordedAnswerIsCorrect?: string
  correctAnswer?: string
  grouping?: string
  score?: number
  badScore?: number
  self_efficacy?: string
  version?: string
  distractors?: string
  question_position?: string
  answer_infix?: string
  answer_prefix?: string
  answer_postfix?: string
  fieldsPrefix?: string
  fieldsPostfix?: string
  upload_extensions?: string[]
  audio?: Audio[]
  record_max_seconds?: number
}
interface OnInit {
  ngOnInit(): void
}

@Component({
    selector: 'problemTemplatePreview',
    templateUrl: './problemTemplatePreview.html',
    styleUrls: ['../../userManagement/userRoleAddEdit/userRoleAddEdit.css', "../../agreement/createEditAgreement/createEditAgreement.css"],
    standalone: false
})
export class ProblemTemplatePreview implements OnInit {
  faChevronLeft = faChevronLeft
  faChevronDown = faChevronDown
  learningObjectiveID: number = -1
  ccl_id: number = -1
  subject_id: number = -1
  problem_template: FullProblemTemplate | null = null
  answer: string = ""
  frolaFormula: string = ""
  _snackBar = inject(MatSnackBar)
  previewDivStyle = `border-color: ${localStorage.getItem('primaryColor')}; border-width: 1px; border-radius: 0.25rem; margin-top: 1rem; padding: 1rem;`
  checkForHTMLTags = checkForHTMLTags
  constructor(private router: Router, private route: ActivatedRoute, private contentService: ContentService) {
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['learningobjective_id'] != null)
        this.learningObjectiveID = params['learningobjective_id']
      if (params['ccl_id'] != null)
        this.ccl_id = params['ccl_id']
      if (params['subject_id'] != null)
        this.subject_id = params['subject_id']
      if (params['problemtemplate_id'] != null) {
        this.contentService.getProblemTemplatePreview(params['problemtemplate_id']).subscribe((response) => {
          if(response.success) {
            this.problem_template = response.result[0]
          } else {
            this._snackBar.open(response.msg, 'Dismiss', {panelClass: ['error-snackbar']})
          }
        })
      }
    })
  }

  exitPreview() {
    this.router.navigate(['dashboard/ContentViewer/SubjectList/CCLTree/LearningObjectives/ProblemTemplates'], {queryParams: {learningobjective_id: this.learningObjectiveID, ccl_id: this.ccl_id, subject_id: this.subject_id}});
  }

  decode(text:string){
    return decodeURIComponent(text)
  }

  public options: Object = {
    // Define the toolbar options for the froala editor.
    toolbarButtons: [
      'undo',
      'redo',
      'bold',
      'italic',
      '|',
      'wirisEditor',
      'wirisChemistry',
      'insertImage'
    ],
    // Add [MW] uttons to the image editing popup Toolbar.
    imageEditButtons: [
      'wirisEditor',
      'wirisChemistry',
      'imageDisplay',
      'imageAlign',
      'imageInfo',
      'imageRemove'
    ],
    // Allow all the tags to understand the mathml
    htmlAllowedTags:  ['.*'],
    htmlAllowedAttrs: ['.*'],
    // List of tags that are not removed when they have no content inside
    // so that formulas renderize propertly
    htmlAllowedEmptyTags: ['mprescripts', 'none'],
    // In case you are using a different Froala editor language than default,
    // language: 'es',
    // You can choose the language for the MathType editor, too:
    // @see: https://docs.wiris.com/en/mathtype/mathtype_web/sdk-api/parameters#regional_properties
    // mathTypeParameters: {
    //   editorParameters: { language: 'es' },
    // },
  };
}
